import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Eye, EyeOff, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import api from "@/lib/api";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("form"); // form, success, error

  useEffect(() => {
    if (!token) setStatus("error");
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters.");
      return;
    }
    if (password !== confirmPassword) {
      toast.error("Passwords don't match.");
      return;
    }
    setLoading(true);
    try {
      const res = await api.post("/auth/reset-password", { token, new_password: password });
      setStatus("success");
      toast.success("Password reset successfully!");
    } catch (err) {
      const detail = err.response?.data?.detail || "";
      if (detail.toLowerCase().includes("expired")) {
        toast.error("This reset link has expired. Please request a new one.");
        setStatus("error");
      } else if (detail.toLowerCase().includes("invalid") || detail.toLowerCase().includes("already")) {
        toast.error("This reset link is invalid or has already been used.");
        setStatus("error");
      } else {
        toast.error(detail || "Failed to reset password. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <div className="max-w-md w-full">
        <button
          onClick={() => navigate("/login")}
          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6"
          data-testid="back-to-login"
        >
          <ArrowLeft className="w-4 h-4" />Back to Login
        </button>

        <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
          {status === "form" && (
            <div className="p-8">
              <div className="flex items-center gap-3 mb-1">
                <img src="/logo.png" alt="SetPlay" className="h-10 w-10" />
                <h1 className="font-heading text-2xl font-bold uppercase tracking-tight">New Password</h1>
              </div>
              <p className="text-muted-foreground text-sm mb-6">
                Enter your new password below.
              </p>

              <form onSubmit={handleSubmit} className="space-y-4" data-testid="reset-password-form">
                <div className="relative">
                  <Label className="text-xs font-heading uppercase tracking-wider">New Password</Label>
                  <Input
                    data-testid="reset-password"
                    type={showPw ? "text" : "password"}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="Min 6 characters"
                    className="h-11 rounded-none border-2 pr-10 mt-1"
                    required
                    minLength={6}
                  />
                  <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-8 text-muted-foreground">
                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>

                <div>
                  <Label className="text-xs font-heading uppercase tracking-wider">Confirm Password</Label>
                  <Input
                    data-testid="reset-confirm-password"
                    type={showPw ? "text" : "password"}
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                    placeholder="Re-enter password"
                    className="h-11 rounded-none border-2 mt-1"
                    required
                    minLength={6}
                  />
                </div>

                <Button
                  data-testid="reset-submit"
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 bg-court-orange hover:bg-court-orange-hover text-white rounded-full font-heading uppercase tracking-wide shadow-[3px_3px_0px_0px_rgba(0,0,0,1)]"
                >
                  {loading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Resetting...</>
                  ) : (
                    <>Reset Password<ArrowRight className="w-4 h-4 ml-2" /></>
                  )}
                </Button>
              </form>
            </div>
          )}

          {status === "success" && (
            <div className="p-8 text-center" data-testid="reset-success">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="font-heading text-xl font-bold mb-2">Password Reset!</h2>
              <p className="text-muted-foreground text-sm mb-6">
                Your password has been reset successfully. You can now log in with your new password.
              </p>
              <Button
                onClick={() => navigate("/login")}
                className="w-full h-12 bg-court-orange hover:bg-court-orange-hover text-white rounded-full font-heading uppercase tracking-wide shadow-[3px_3px_0px_0px_rgba(0,0,0,1)]"
                data-testid="reset-go-login"
              >
                Login<ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}

          {status === "error" && (
            <div className="p-8 text-center" data-testid="reset-error">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-8 h-8 text-red-600" />
              </div>
              <h2 className="font-heading text-xl font-bold mb-2">Invalid Reset Link</h2>
              <p className="text-muted-foreground text-sm mb-6">
                This password reset link is invalid or has expired. Please request a new one.
              </p>
              <div className="space-y-3">
                <Button
                  onClick={() => navigate("/forgot-password")}
                  className="w-full h-12 bg-court-orange hover:bg-court-orange-hover text-white rounded-full font-heading uppercase tracking-wide shadow-[3px_3px_0px_0px_rgba(0,0,0,1)]"
                  data-testid="reset-request-new"
                >
                  Request New Reset Link<ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => navigate("/login")}
                  className="w-full text-sm"
                >
                  Back to Login
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
